import React, { useState, useEffect } from 'react';
import {
  Card,
  Tree,
  Table,
  Button,
  Space,
  Modal,
  Select,
  Switch,
  message,
  Typography,
  Tag,
  Empty,
  Row,
  Col,
  Divider,
  Tabs,
  Checkbox,
  Alert
} from 'antd';
import {
  SettingOutlined,
  UserOutlined,
  TeamOutlined,
  SecurityScanOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';

import type { User, UserPermission, PermissionGroup, Program } from '../../../types/user';
import { UserService } from '../../../services/userService';
import { useAuth } from '../../../contexts/AuthContext';
import { PERMISSION_ACTIONS } from '../../../types/user';

const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface PermissionTabProps {
  user: User | null;
  mode: 'create' | 'edit' | 'view';
  onChange: (data: Partial<User>) => void;
}

interface PermissionData {
  personalPermissions: UserPermission[];
  groupPermissions: UserPermission[];
  effectivePermissions: UserPermission[];
  availableGroups: PermissionGroup[];
  programs: Program[];
}

const PermissionTab: React.FC<PermissionTabProps> = ({
  user,
  mode,
  onChange
}) => {
  const { refreshPermissions } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('effective');
  const [permissionData, setPermissionData] = useState<PermissionData>({
    personalPermissions: [],
    groupPermissions: [],
    effectivePermissions: [],
    availableGroups: [],
    programs: [],
  });

  const isReadOnly = mode === 'view';

  // Load permission data
  useEffect(() => {
    if (mode !== 'create' && user?.userId) {
      loadPermissionData();
    } else {
      loadInitialData();
    }
  }, [mode, user?.userId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [groups, programs] = await Promise.all([
        UserService.listPermissionGroups(),
        UserService.listPrograms(),
      ]);

      setPermissionData(prev => ({
        ...prev,
        availableGroups: groups.items,
        programs,
      }));
    } catch (error) {
      console.error('載入權限資料失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissionData = async () => {
    if (!user?.userId) return;

    try {
      setLoading(true);
      const [
        userPermissions,
        effectivePermissions,
        groups,
        programs
      ] = await Promise.all([
        UserService.getUserPermissions(user.userId),
        UserService.getUserEffectivePermissions(user.userId),
        UserService.listPermissionGroups(),
        UserService.listPrograms(),
      ]);

      setPermissionData({
        personalPermissions: userPermissions.personalPermissions || [],
        groupPermissions: userPermissions.groupPermissions || [],
        effectivePermissions: effectivePermissions.effectivePermissions || [],
        availableGroups: groups.items,
        programs,
      });
    } catch (error) {
      console.error('載入權限資料失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle permission change
  const handlePermissionChange = async (programId: number, action: string, value: boolean) => {
    if (mode === 'create' || !user?.userId) {
      // For create mode, update local state
      const updatedPermissions = [...permissionData.personalPermissions];
      const existingIndex = updatedPermissions.findIndex(p => p.programId === programId);
      
      if (existingIndex >= 0) {
        updatedPermissions[existingIndex] = {
          ...updatedPermissions[existingIndex],
          [action]: value,
        };
      } else {
        const program = permissionData.programs.find(p => p.pgId === programId);
        if (program) {
          updatedPermissions.push({
            programId,
            programName: program.pgName,
            addNew: action === 'addNew' ? value : false,
            edit: action === 'edit' ? value : false,
            delete: action === 'delete' ? value : false,
            export: action === 'export' ? value : false,
            print: action === 'print' ? value : false,
            query: action === 'query' ? value : false,
            approve: action === 'approve' ? value : false,
          });
        }
      }

      setPermissionData(prev => ({
        ...prev,
        personalPermissions: updatedPermissions,
      }));

      onChange({ permissions: updatedPermissions });
    } else {
      // For edit mode, call API
      try {
        const updatedPermissions = [...permissionData.personalPermissions];
        const existingIndex = updatedPermissions.findIndex(p => p.programId === programId);

        if (existingIndex >= 0) {
          updatedPermissions[existingIndex] = {
            ...updatedPermissions[existingIndex],
            [action]: value,
          };
        } else {
          const program = permissionData.programs.find(p => p.pgId === programId);
          if (program) {
            updatedPermissions.push({
              programId,
              programName: program.pgName,
              addNew: action === 'addNew' ? value : false,
              edit: action === 'edit' ? value : false,
              delete: action === 'delete' ? value : false,
              export: action === 'export' ? value : false,
              print: action === 'print' ? value : false,
              query: action === 'query' ? value : false,
              approve: action === 'approve' ? value : false,
            });
          }
        }

        await UserService.setUserPermissions(user.userId, {
          permissions: updatedPermissions.map(p => ({
            programId: p.programId,
            addNew: p.addNew,
            edit: p.edit,
            delete: p.delete,
            export: p.export,
            print: p.print,
            query: p.query,
            approve: p.approve,
          })),
        });

        message.success('權限更新成功');
        await refreshPermissions();
        loadPermissionData(); // Reload to get effective permissions
      } catch (error) {
        message.error(`權限更新失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
      }
    }
  };

  // Convert programs to tree data
  const getPermissionTreeData = (programs: Program[]): DataNode[] => {
    return programs.map(program => ({
      key: program.pgId,
      title: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{program.pgName}</span>
          <Space size="small">
            {Object.entries(PERMISSION_ACTIONS).map(([key, action]) => {
              const permission = permissionData.effectivePermissions.find(p => p.programId === program.pgId);
              const hasPermission = permission?.[action as keyof UserPermission] as boolean;
              
              return (
                <Checkbox
                  key={action}
                  checked={hasPermission}
                  onChange={(e) => handlePermissionChange(program.pgId, action, e.target.checked)}
                  disabled={isReadOnly}
                  style={{ fontSize: '12px' }}
                >
                  {key.replace('_', '')}
                </Checkbox>
              );
            })}
          </Space>
        </div>
      ),
      children: program.children ? getPermissionTreeData(program.children) : undefined,
    }));
  };

  // Permission table columns
  const permissionColumns: ColumnsType<UserPermission> = [
    {
      title: '程式名稱',
      dataIndex: 'programName',
      key: 'programName',
      width: 200,
    },
    {
      title: '新增',
      dataIndex: 'addNew',
      key: 'addNew',
      width: 80,
      align: 'center',
      render: (value: boolean) => value ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CloseOutlined style={{ color: '#ff4d4f' }} />,
    },
    {
      title: '編輯',
      dataIndex: 'edit',
      key: 'edit',
      width: 80,
      align: 'center',
      render: (value: boolean) => value ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CloseOutlined style={{ color: '#ff4d4f' }} />,
    },
    {
      title: '刪除',
      dataIndex: 'delete',
      key: 'delete',
      width: 80,
      align: 'center',
      render: (value: boolean) => value ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CloseOutlined style={{ color: '#ff4d4f' }} />,
    },
    {
      title: '匯出',
      dataIndex: 'export',
      key: 'export',
      width: 80,
      align: 'center',
      render: (value: boolean) => value ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CloseOutlined style={{ color: '#ff4d4f' }} />,
    },
    {
      title: '列印',
      dataIndex: 'print',
      key: 'print',
      width: 80,
      align: 'center',
      render: (value: boolean) => value ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CloseOutlined style={{ color: '#ff4d4f' }} />,
    },
    {
      title: '查詢',
      dataIndex: 'query',
      key: 'query',
      width: 80,
      align: 'center',
      render: (value: boolean) => value ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CloseOutlined style={{ color: '#ff4d4f' }} />,
    },
  ];

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Space align="center">
            <SettingOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>權限設定</Title>
              <Text type="secondary">管理使用者的系統存取權限</Text>
            </div>
          </Space>
        </Col>
      </Row>

      <Divider />

      {/* Permission Information */}
      {mode !== 'create' && (
        <Alert
          message="權限說明"
          description="有效權限 = 群組權限 + 個人權限的聯集。個人權限可以覆蓋或擴展群組權限。"
          type="info"
          style={{ marginBottom: '24px' }}
          showIcon
        />
      )}

      {/* Permission Tabs */}
      <Card>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'effective',
              label: (
                <Space>
                  <SecurityScanOutlined />
                  <span>有效權限</span>
                </Space>
              ),
              children: (
                <div>
                  <Text type="secondary" style={{ marginBottom: '16px', display: 'block' }}>
                    顯示使用者的最終有效權限（群組權限 + 個人權限）
                  </Text>
                  <Table
                    columns={permissionColumns}
                    dataSource={permissionData.effectivePermissions}
                    rowKey="programId"
                    loading={loading}
                    pagination={false}
                    size="small"
                    locale={{
                      emptyText: (
                        <Empty
                          description="尚未設定權限"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      )
                    }}
                  />
                </div>
              ),
            },
            {
              key: 'personal',
              label: (
                <Space>
                  <UserOutlined />
                  <span>個人權限</span>
                </Space>
              ),
              children: (
                <div>
                  <Text type="secondary" style={{ marginBottom: '16px', display: 'block' }}>
                    設定使用者的個人專屬權限
                  </Text>
                  {!isReadOnly && (
                    <Tree
                      treeData={getPermissionTreeData(permissionData.programs)}
                      showLine
                      showIcon={false}
                      defaultExpandAll={false}
                      height={400}
                      style={{ marginBottom: '16px' }}
                    />
                  )}
                  <Table
                    columns={permissionColumns}
                    dataSource={permissionData.personalPermissions}
                    rowKey="programId"
                    loading={loading}
                    pagination={false}
                    size="small"
                    locale={{
                      emptyText: (
                        <Empty
                          description="尚未設定個人權限"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      )
                    }}
                  />
                </div>
              ),
            },
            {
              key: 'group',
              label: (
                <Space>
                  <TeamOutlined />
                  <span>群組權限</span>
                </Space>
              ),
              children: (
                <div>
                  <Text type="secondary" style={{ marginBottom: '16px', display: 'block' }}>
                    顯示從權限群組繼承的權限
                  </Text>
                  <Table
                    columns={permissionColumns}
                    dataSource={permissionData.groupPermissions}
                    rowKey="programId"
                    loading={loading}
                    pagination={false}
                    size="small"
                    locale={{
                      emptyText: (
                        <Empty
                          description="尚未加入權限群組"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      )
                    }}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 600, color: '#1890ff' }}>
              {permissionData.effectivePermissions.length}
            </div>
            <div style={{ color: '#8c8c8c' }}>有效權限</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 600, color: '#52c41a' }}>
              {permissionData.personalPermissions.length}
            </div>
            <div style={{ color: '#8c8c8c' }}>個人權限</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 600, color: '#fa8c16' }}>
              {permissionData.groupPermissions.length}
            </div>
            <div style={{ color: '#8c8c8c' }}>群組權限</div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PermissionTab;